// Pure helpers for the /app/connect page (unit tested in test/connect.test.ts).

import {
    CONNECT_STATE_RE,
    PKCE_CHALLENGE_RE,
    SUB_TENANT_RE,
    connectRedirect,
} from '@yourownmetric/shared'

export interface ConnectParams {
    platform: string
    store: string
    returnUrl: string
    state: string
    codeChallenge: string
}

/**
 * The link's query → params, or a reason it can't be used. The API re-validates everything
 * (platform, store, return-URL allowlist); this only catches links that are plainly broken.
 */
export function parseConnectParams(q: URLSearchParams): ConnectParams | { error: string } {
    const platform = q.get('platform') ?? ''
    const store = q.get('store') ?? ''
    const returnUrl = q.get('return') ?? ''
    const state = q.get('state') ?? ''
    const codeChallenge = q.get('code_challenge') ?? ''
    if (!platform || !store || !returnUrl) {
        return { error: 'This link is missing the platform, store or return address.' }
    }
    if (!CONNECT_STATE_RE.test(state)) {
        return { error: 'This link is missing its security state. Start again from the app.' }
    }
    if (q.get('code_challenge_method') !== 'S256' || !PKCE_CHALLENGE_RE.test(codeChallenge)) {
        return { error: 'This link is missing its security challenge. Start again from the app.' }
    }
    return { platform, store, returnUrl, state, codeChallenge }
}

/** Where "Cancel" goes: the (server-validated) return URL with error=access_denied. */
export const cancelUrl = (p: ConnectParams) =>
    connectRedirect(p.returnUrl, { state: p.state, error: 'access_denied' })

/** Suggested sub-tenant id for a store (a valid external id, ≤64 chars). */
export function suggestedSubTenant(store: string): string {
    const id = store
        .toLowerCase()
        .replace(/[^a-z0-9_.:-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
    return SUB_TENANT_RE.test(id) ? id : ''
}

/** Default domain for a new project named after the store (host-type stores only). */
export function suggestedDomain(store: string): string {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(store) ? store.toLowerCase() : ''
}

export const isSubTenantId = (s: string) => SUB_TENANT_RE.test(s)
